import { Directive, HostListener, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { GeneralStateAction } from '../state/pageState/page-state.actions';

/**
 * The url of `path` on another subdomain, or null when no switch is needed (no subdomain given,
 * already on it, or localhost) - then the caller stays on the current host.
 */
export function subDomainUrl(subDomain: string | null | undefined, path: string, suffix = ''): string | null {
  // Empty or null (the API sends null when it doesn't know) means the current host.
  if (!subDomain) return null;

  const { protocol, hostname } = window.location;
  const parts = hostname.split('.');

  if (parts[0] === 'localhost') return null;

  if (parts[0].toLowerCase() === subDomain.toLowerCase()) return null;

  if (parts.length > 2) parts[0] = subDomain;
  else parts.unshift(subDomain);

  const newHost = parts.join('.').replace(/^\./, '');

  return `${protocol}//${newHost}${path}${suffix}`;
}

@Directive({
  selector: '[routerLinkSubDomain]',
  standalone: true
})
export class RouterLinkSubDomainDirective {
  @Input('routerLinkSubDomain') subDomain?: string;
  @Input() routerLink?: string | any[];
  router: Router = inject(Router);
  store: Store = inject(Store);

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    const newUrl = this.buildNewUrl();
    if (!newUrl) return; // Same subdomain or localhost — let RouterLink handle it

    event.preventDefault();

    if (event.ctrlKey || event.metaKey) {
      window.open(newUrl, '_blank');
      return;
    }

    this.store.dispatch(GeneralStateAction.setShowRedirectScreenState({ showRedirectScreen: true }));
    window.location.href = newUrl;
  }

  private buildNewUrl(): string | null {
    let newPath = window.location.pathname;
    if (this.routerLink) {
      const tree = this.router.createUrlTree(
        Array.isArray(this.routerLink) ? this.routerLink : [this.routerLink]
      );
      newPath = this.router.serializeUrl(tree);
    }

    return subDomainUrl(this.subDomain, newPath, `${window.location.search}${window.location.hash}`);
  }
}
